<template lang="html">
  <div>
    <v-form v-model="formularioValido" ref="form">
      <v-container>
        
        <!-- Título e Sigla -->
        <v-layout row wrap justify-space-between>

          <!-- Título -->
          <v-flex xs9>
            <v-text-field
              label='Título*'
              v-model="projetoPesquisa.titulo"
              counter="400"
              :rules="[...obrigatorio,...max400caracteres]"
            >
            </v-text-field>
          </v-flex>

          <!-- Sigla -->
          <v-flex xs2>
            <v-text-field
              label='Sigla'
              v-model="projetoPesquisa.sigla"
              counter="20"
              :rules="max20caracteres"
            >
            </v-text-field>
          </v-flex>
        </v-layout>

        <!-- Área do Conhecimento e Nome da Área -->
        <v-layout row wrap justify-space-between>

          <!-- Área do Conhecimento -->
          <v-flex xs11 sm5>
            <v-select
              label='Área do Conhecimento*'
              :items="listaAreaDoConhecimento"
              v-model="projetoPesquisa.areaConhecimento"
              return-object
              item-text="descricao"
              item-value="id"
              :rules="obrigatorio"
              hint="O projeto deve ser registrado na área do conhecimento pertinente a sua temática."
              @input='getNomesDasAreas(projetoPesquisa.areaConhecimento.id)'                  
            >
            </v-select>
          </v-flex>

          <!-- Nome da Área -->
          <v-flex xs11 sm5>
            <v-select
              label='Nome da Área*'
              :items="listaNomeArea"
              v-model="projetoPesquisa.nomeArea"
              return-object
              item-text="descricao"
              item-value="id"
              :rules="obrigatorio"
              :disabled="nomeAreaDisabled"
              @input='getNomesDasSubareas(projetoPesquisa.nomeArea.id)' 
            >
            </v-select>
          </v-flex>

        </v-layout>

        <!-- Nome da Subárea e Linha de Pesquisa no Programa de Pós-Graduação -->
        <v-layout row wrap justify-space-between>

          <!-- Nome da Subárea -->
          <v-flex xs11 sm5>
            <v-select
              label='Nome da Subárea*'
              :items="listaNomeSubarea"
              v-model="projetoPesquisa.nomeSubarea"
              return-object
              item-text="descricao"
              item-value="id"
              :rules="obrigatorio"
              :disabled="nomeSubareaDisabled || listaNomeSubarea.length === 0"
            >
            </v-select>
          </v-flex>

          <!-- Linha de Pesquisa no Programa de Pós-Graduação -->
          <v-flex xs11 sm5>
            <v-select
              label='Linha de Pesquisa no Programa de Pós-Graduação*'
              :items="listaLinhaPesquisaPPG"
              v-model="projetoPesquisa.linhaPesquisaPPG"
              return-object
              item-text="descricao"
              item-value="id"
              disabled
              hint="Esta opção está indisponível no momento."
              persistent-hint
            >
            </v-select>
          </v-flex>

        </v-layout>

        <!-- Data de início e Prazo em meses -->
        <v-layout row wrap justify-space-between>

          <!-- Data de início -->
          <v-flex xs11 sm5>
            <c-date-dialog
              label="Data de início*"
              v-model="projetoPesquisa.dataInicio"
              :rules="obrigatorio"
            >
            </c-date-dialog>
          </v-flex>

          <v-spacer></v-spacer>

          <!-- Prazo em meses -->
          <v-flex xs11 sm5>
            <v-text-field-integer
              label='Prazo em meses*'
              v-model="projetoPesquisa.prazoMeses"
              v-bind:properties="{
                rules: [...obrigatorio,...numerico]
              }"
            />
          </v-flex>
        </v-layout>

        <!-- Palavras-chaves -->
        <v-layout row wrap justify-space-between>
          <v-flex xs12>
            <v-textarea
              label="Palavras-chaves*"
              v-model="projetoPesquisa.palavrasChaves"
              counter="300"
              :rows="1"
              :auto-grow="true"
              :rules="[...obrigatorio,...max300caracteres,...palavrasChaves]"
              hint="Indique no mínimo 03 e no máximo 06 palavras que podem ser relacionadas ao escopo do projeto. As palavras devem ser separadas somente por vírgulas e sem ponto ao final."
            >
            </v-textarea>
          </v-flex>
        </v-layout>

        <!-- Resumo -->
        <v-layout row wrap justify-space-between>
          <v-flex xs12>
            <v-textarea
              label="Resumo*"
              v-model="projetoPesquisa.resumo"
              counter="2000"
              :rows="4"
              :auto-grow="true"
              :rules="[...obrigatorio,...max2000caracteres]"
              hint="Deve conter uma descrição do projeto, incluindo sua relevância."
            >
            </v-textarea>
          </v-flex>
        </v-layout>

        <br>

      </v-container>
    </v-form>

    <v-divider></v-divider>

    <v-card-actions>

      <!-- Botão "Cancelar" -->
      <v-btn color="primary" @click="cancelar()">Cancelar</v-btn>

      <v-spacer></v-spacer>

      <!-- Botão "Salvar" -->
      <v-btn color="primary" :disabled="!formularioValido" @click="editarProjetoPesquisa()">Salvar</v-btn>

    </v-card-actions>

  </div>
</template>

<script>
  import projetos from '@/utils/projetos'
  import BasicToolbar from '@/components/toolbars/basic-toolbar'
  import DataDialog from '@/components/c-date-dialog'
  import TextArea from '@/components/c-textarea'
  import dates from '@/utils/dates'
  import { mapActions } from 'vuex'

  export default {
    props: ['idProjetoPesquisa'],
    data: () => ({
      formularioValido: false,
      dates: dates,
      projetoPesquisa: {},
      listaAreaDoConhecimento: [],
      listaNomeArea: [],
      listaNomeSubarea: [],
      listaLinhaPesquisaPPG: [],
      nomeAreaDisabled: false,
      nomeSubareaDisabled: false,
      obrigatorio: [v => !!v || 'Este campo é obrigatório'],
      numerico: [
        (input) => !isNaN(input) || 'Este campo deve ser numérico',
        (input) => (input.length === 0 || Number(input) !== 0) || 'Este campo não pode ser nulo',
        (input) => (input.length === 0 || Number(input) >= 0) || 'Este campo não pode ter valor negativo'
      ],
      max20caracteres: [v => v.length <= 20 || 'Este campo pode ter no máximo 20 caracteres'],
      max50caracteres: [v => v.length <= 50 || 'Este campo pode ter no máximo 50 caracteres'],
      max300caracteres: [v => v.length <= 300 || 'Este campo pode ter no máximo 300 caracteres'],
      max400caracteres: [v => v.length <= 400 || 'Este campo pode ter no máximo 400 caracteres'],
      max2000caracteres: [v => v.length <= 2000 || 'Este campo pode ter no máximo 2000 caracteres'],
      palavrasChaves: [v => /^(?:[^\s.,:;][a-zA-Z0-9-áéíóúàèìòùâêôûãõçÁÉÍÓÚÀÈÌÒÙÂÊÔÛÃÕÇ\s]+[^\s.,:;],){1}(?:\s?[^\s.,:;][a-zA-Z0-9-áéíóúàèìòùâêôûãõçÁÉÍÓÚÀÈÌÒÙÂÊÔÛÃÕÇ\s]+[^\s.,:;],){1,4}(?:\s?[^\s.,:;][a-zA-Z0-9-áéíóúàèìòùâêôûãõçÁÉÍÓÚÀÈÌÒÙÂÊÔÛÃÕÇ\s]+[^\s.,:;]){1}$/.test(v) || 'O projeto deve conter no mínimo 03 e no máximo 06 palavras-chaves separadas apenas por vírgulas e sem ponto ao final.']
    }),
    methods: {
      ...mapActions('ui', ['SHOW_GLOBAL_LOADING', 'HIDE_GLOBAL_LOADING', 'SHOW_SNACKBAR']),

      // Edita um projeto de pesquisa.
      editarProjetoPesquisa: function () {
        this.SHOW_GLOBAL_LOADING({ globalLoadingTitle: 'Editando Projeto de Pesquisa...' })
        projetos.putProjetoPesquisa(this.idProjetoPesquisa, this.projetoPesquisa)
        .then(response => {
          this.SHOW_SNACKBAR({snackbarType: 'success', snackbarText: response.body.mensagem})
          this.$router.push({name: 'ProjetoPesquisaInformacoesBasicas', params: {idProjetoPesquisa: this.idProjetoPesquisa}})
        })
        .catch(error => {
          this.SHOW_SNACKBAR({snackbarType: 'error', snackbarText: error.body.mensagem})
        })
        .finally(() => {
          this.HIDE_GLOBAL_LOADING({ globalLoadingTitle: 'Editando Projeto de Pesquisa...' })
        })
      },

      // Redireciona para a tela de visualização da aba "Informações Básicas".
      cancelar: function () {
        this.$router.push({name: 'ProjetoPesquisaInformacoesBasicas', params: {idProjetoPesquisa: this.idProjetoPesquisa}})
      },

      // Carrega as áreas.
      getNomesDasAreas: function (idClassifSup) {
        this.obtemNomesDasAreas(idClassifSup, this, 'listaNomeArea')
        this.nomeAreaDisabled = false
        this.listaNomeSubarea = []
        this.nomeSubareaDisabled = true
      },

      // Carrega as subáreas.
      getNomesDasSubareas: function (idClassifSup) {
        this.obtemNomesDasSubareas(idClassifSup, this, 'listaNomeSubarea')
        this.nomeSubareaDisabled = false
      },

      // Carrega as áreas do conhecimento do CNPq.
      obtemAreasDoConhecimento: function (aqui, atributo) {
        this.SHOW_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando áreas do conhecimento...' })
        projetos.getClassificacoesAreasDoConhecimento()
        .then(response => {
          aqui[atributo] = response.data.slice()
        })
        .catch(error => {
          console.log('Erro ao mandar o request' + error)
        })
        .finally(() => {
          this.HIDE_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando áreas do conhecimento...' })
        })
      },

      // Carrega os nomes das áreas.
      obtemNomesDasAreas: function (idClassifSup, aqui, atributo) {
        this.SHOW_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando nomes das áreas...' })
        projetos.getClassificacoesClassificacaoSuperior(idClassifSup)
        .then(response => {
          aqui[atributo] = response.data.slice()
        })
        .catch(error => {
          console.log('Erro ao mandar o request' + error)
        })
        .finally(() => {
          this.HIDE_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando nomes das áreas...' })
        })
      },

      // Carrega os nomes das subáreas.
      obtemNomesDasSubareas: function (idClassifSup, aqui, atributo) {
        this.SHOW_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando nomes das subáreas...' })
        projetos.getClassificacoesClassificacaoSuperior(idClassifSup)
        .then(response => {
          aqui[atributo] = response.data.slice()
        })
        .catch(error => {
          console.log('Erro ao mandar o request' + error)
        })
        .finally(() => {
          this.HIDE_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando nomes das subáreas...' })
        })
      }
    },
    mounted () {
      // Carrega o projeto de pesquisa.
      this.SHOW_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando projeto de pesquisa...' })
      projetos.getProjetoPesquisa(this.idProjetoPesquisa)
      .then(response => {
        this.projetoPesquisa = response.data
        // Carrega os nomes das áreas.
        this.obtemNomesDasAreas(this.projetoPesquisa.areaConhecimento.id, this, 'listaNomeArea')
        // Carrega os nomes das subáreas.
        this.obtemNomesDasSubareas(this.projetoPesquisa.nomeArea.id, this, 'listaNomeSubarea')
      })
      .catch(error => {
        console.log('Erro ao mandar o request' + error)
      })
      .finally(() => {
        this.HIDE_GLOBAL_LOADING({ globalLoadingTitle: 'Carregando projeto de pesquisa...' })
      })

      // Carrega as áreas do conhecimento do CNPq.
      this.obtemAreasDoConhecimento(this, 'listaAreaDoConhecimento')
    },
    components: {
      'basic-toolbar': BasicToolbar,
      'c-date-dialog': DataDialog,
      'c-textarea': TextArea
    }
  }
</script>

<style lang="css">
  .menu {
    display: block !important;
  }

  .no-resize div textarea{
    resize: none;
  }
  .input-group__icon-cb {
    cursor: pointer !important;
  }
</style>



// WEBPACK FOOTER //
// editar-informacoes-basicas.vue?171551ac